import { DashboardCard } from "@dashboard/components/Card";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import { TableButtonWrapper } from "@dashboard/components/TableButtonWrapper/TableButtonWrapper";
import TableRowLink from "@dashboard/components/TableRowLink";
import { staffMemberDetailsUrl } from "@dashboard/staff/urls";
import { TableBody, TableCell, TableHead } from "@material-ui/core";
import { Box, Button, Checkbox, Skeleton, Text, TrashBinIcon } from "@saleor/macaw-ui-next";
import { FC } from "react";
import { FormattedMessage } from "react-intl";

import { StaffNotificationRecipient } from "../../types";
import { messages } from "../NewsletterListPage/messages";

interface StaffRecipientsListProps {
  recipients: StaffNotificationRecipient[] | undefined;
  disabled?: boolean;
  onAdd: () => void;
  onToggle: (recipient: StaffNotificationRecipient) => void;
  onDelete: (recipient: StaffNotificationRecipient) => void;
}

const numberOfColumns = 4;

export const StaffRecipientsList: FC<StaffRecipientsListProps> = ({
  recipients,
  disabled,
  onAdd,
  onToggle,
  onDelete,
}) => (
  <DashboardCard>
    <DashboardCard.Header>
      <Box>
        <DashboardCard.Title>
          <FormattedMessage {...messages.staffTitle} />
        </DashboardCard.Title>
        <DashboardCard.Subtitle>
          <FormattedMessage {...messages.staffSubtitle} />
        </DashboardCard.Subtitle>
      </Box>
      <DashboardCard.Toolbar>
        <Button variant="secondary" onClick={onAdd} data-test-id="add-recipient">
          <FormattedMessage {...messages.addRecipient} />
        </Button>
      </DashboardCard.Toolbar>
    </DashboardCard.Header>
    <ResponsiveTable data-test-id="staff-recipients-list">
      <TableHead>
        <TableRowLink>
          <TableCell>
            <FormattedMessage {...messages.colEmail} />
          </TableCell>
          <TableCell>
            <FormattedMessage {...messages.colUser} />
          </TableCell>
          <TableCell>
            <FormattedMessage {...messages.colStatus} />
          </TableCell>
          <TableCell>
            <FormattedMessage {...messages.colActions} />
          </TableCell>
        </TableRowLink>
      </TableHead>
      <TableBody>
        {recipients === undefined ? (
          <TableRowLink>
            <TableCell colSpan={numberOfColumns}>
              <Skeleton />
            </TableCell>
          </TableRowLink>
        ) : recipients.length === 0 ? (
          <TableRowLink data-test-id="empty-list-message">
            <TableCell colSpan={numberOfColumns}>
              <FormattedMessage {...messages.noRecipients} />
            </TableCell>
          </TableRowLink>
        ) : (
          recipients.map(recipient => (
            <TableRowLink
              key={recipient.id}
              href={recipient.user ? staffMemberDetailsUrl(recipient.user.id) : undefined}
              hover={!!recipient.user}
              data-test-id={"recipient-" + recipient.id}
            >
              <TableCell>
                <Text>{recipient.email ?? recipient.user?.email ?? "-"}</Text>
              </TableCell>
              <TableCell>
                <Text>
                  {recipient.user ? (
                    `${recipient.user.firstName} ${recipient.user.lastName}`.trim() ||
                    recipient.user.email
                  ) : (
                    <FormattedMessage {...messages.notLinked} />
                  )}
                </Text>
              </TableCell>
              <TableCell>
                <Text color={recipient.active ? "default1" : "defaultDisabled"}>
                  <FormattedMessage
                    {...(recipient.active ? messages.statusActive : messages.statusInactive)}
                  />
                </Text>
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" gap={3}>
                  <TableButtonWrapper>
                    <Checkbox
                      checked={!!recipient.active}
                      disabled={disabled}
                      onCheckedChange={() => onToggle(recipient)}
                      data-test-id={"toggle-recipient-" + recipient.id}
                    />
                  </TableButtonWrapper>
                  <TableButtonWrapper>
                    <Button
                      icon={<TrashBinIcon />}
                      variant="secondary"
                      disabled={disabled}
                      onClick={() => onDelete(recipient)}
                      data-test-id={"delete-recipient-" + recipient.id}
                    />
                  </TableButtonWrapper>
                </Box>
              </TableCell>
            </TableRowLink>
          ))
        )}
      </TableBody>
    </ResponsiveTable>
  </DashboardCard>
);
